import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, storage } from "../firebase";
import { useAdminAuth } from "../context/AdminAuthContext";
import useGameCatalog from "../hooks/useGameCatalog";

const normalize = (value) => String(value || "").toLowerCase();

const getProfileDisplayName = (profile, user) => {
  const value =
    profile?.name ||
    profile?.displayName ||
    profile?.fullName ||
    user?.displayName ||
    "";
  return String(value || "");
};

const getProfileUsername = (profile) => {
  const value = profile?.username || profile?.handle || profile?.userName || "";
  return String(value || "");
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value === "number") return new Date(value);
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : new Date(parsed);
};

const formatDateTime = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleString("en-GB") : "N/A";
};

const formatTime = (value) => {
  const date = toDate(value);
  return date
    ? date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : "";
};

const formatDateTimeInput = (value) => {
  const date = toDate(value);
  if (!date) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const parseNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const sanitizeFileName = (name = "avatar") =>
  String(name).replace(/[^a-zA-Z0-9._-]/g, "_");

const uploadCreatorAvatar = (file, ownerId, onProgress) =>
  new Promise((resolve, reject) => {
    const safeName = sanitizeFileName(file?.name || "avatar");
    const filePath = `creator-avatars/${ownerId || "unknown"}/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, filePath);
    const task = uploadBytesResumable(storageRef, file);

    task.on(
      "state_changed",
      (snapshot) => {
        if (typeof onProgress === "function" && snapshot.totalBytes > 0) {
          const percent = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          onProgress(percent);
        }
      },
      (error) => reject(error),
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve(url);
        } catch (error) {
          reject(error);
        }
      }
    );
  });

const getCreatorGames = (creator) => {
  if (!creator) return [];
  if (Array.isArray(creator.games)) return creator.games;
  if (Array.isArray(creator.gameIds)) return creator.gameIds;
  if (Array.isArray(creator.selectedGames)) {
    return creator.selectedGames
      .map((entry) => entry?.selectedGame || entry?.gameId)
      .filter(Boolean);
  }
  if (creator.gameId) return [creator.gameId];
  return [];
};

const getCreatorOwnerId = (creator) =>
  creator?.ownerId || creator?.userId || creator?.creatorUserId || "";

const getCreatorLabel = (creator) =>
  creator?.displayName || creator?.name || creator?.username || "Creator";

const getThreadUserLabel = (thread, userProfiles) => {
  const userId = thread?.userId || thread?.id || "";
  const profile = userId ? userProfiles?.[userId] : null;
  return (
    profile?.username ||
    profile?.name ||
    profile?.displayName ||
    thread?.userName ||
    thread?.username ||
    userId ||
    "User"
  );
};

const buildCreatorDraft = (creator, fallbackOwnerId) => ({
  displayName: creator?.displayName || creator?.name || "",
  username: creator?.username || creator?.handle || "",
  avatar: creator?.avatar || "",
  bio: creator?.bio || creator?.about || "",
  games: getCreatorGames(creator),
  startingPrice:
    creator?.startingPrice ??
    creator?.startingFrom ??
    creator?.price ??
    "",
  priceMin:
    creator?.priceRange?.min ??
    creator?.priceRange?.start ??
    creator?.priceRange?.low ??
    "",
  priceMax:
    creator?.priceRange?.max ??
    creator?.priceRange?.high ??
    "",
  isOnline: Boolean(creator?.isOnline || creator?.online),
  isLive: Boolean(creator?.isLive || creator?.live),
  rating:
    creator?.rating ??
    creator?.ratings?.average ??
    "",
  ownerId: getCreatorOwnerId(creator) || fallbackOwnerId || "",
});

const defaultSessionDraft = (gameId) => ({
  title: "",
  gameId: gameId || "",
  startsAt: "",
  durationMins: "",
  capacity: "",
  price: "",
});

const Creators = () => {
  const { user: currentUser, role, profile } = useAdminAuth();
  const { games, activeGames, getGameName } = useGameCatalog();
  const selectableGames = activeGames.length ? activeGames : games;
  const defaultGameId = selectableGames[0]?.id || games[0]?.id || "";
  const [creators, setCreators] = useState([]);
  const [loadingCreators, setLoadingCreators] = useState(true);
  const [search, setSearch] = useState("");
  const [activeModule, setActiveModule] = useState("profile");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedCreatorId, setSelectedCreatorId] = useState(null);
  const [creatorDraft, setCreatorDraft] = useState(buildCreatorDraft());
  const [creatorEditDraft, setCreatorEditDraft] = useState(buildCreatorDraft());
  const [creatingCreator, setCreatingCreator] = useState(false);
  const [createError, setCreateError] = useState("");
  const [updatingCreator, setUpdatingCreator] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarUploadProgress, setAvatarUploadProgress] = useState(null);
  const [avatarEditFile, setAvatarEditFile] = useState(null);
  const [avatarEditPreview, setAvatarEditPreview] = useState("");
  const [avatarEditUploadProgress, setAvatarEditUploadProgress] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [sessionDraft, setSessionDraft] = useState(
    defaultSessionDraft(defaultGameId)
  );
  const [sessionSaving, setSessionSaving] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [sessionError, setSessionError] = useState("");

  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  const [userProfiles, setUserProfiles] = useState({});
  const [chatThreads, setChatThreads] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChatMessages, setLoadingChatMessages] = useState(false);
  const [chatText, setChatText] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const [chatError, setChatError] = useState("");

  const isCreatorRole = role === "creator";
  const isSuperRole = role === "developer" || role === "super_admin";
  const profileDisplayName = getProfileDisplayName(profile, currentUser).trim();
  const profileUsername = getProfileUsername(profile).trim();
  const lockedDisplayName = isCreatorRole ? profileDisplayName : "";
  const lockedUsername = isCreatorRole ? profileUsername : "";

  const selectedCreator = useMemo(
    () => creators.find((item) => item.id === selectedCreatorId) || null,
    [creators, selectedCreatorId]
  );

  const selectedSession = useMemo(
    () => sessions.find((item) => item.id === selectedSessionId) || null,
    [sessions, selectedSessionId]
  );

  const selectedThread = useMemo(
    () => chatThreads.find((item) => item.id === selectedChatId) || null,
    [chatThreads, selectedChatId]
  );

  useEffect(() => {
    const ids = new Set();
    creators.forEach((creator) => {
      const ownerId = getCreatorOwnerId(creator);
      if (ownerId) ids.add(ownerId);
    });
    bookings.forEach((booking) => {
      if (booking.userId) ids.add(booking.userId);
    });
    chatThreads.forEach((thread) => {
      const threadUserId = thread.userId || thread.id;
      if (threadUserId) ids.add(threadUserId);
    });
    if (selectedThread?.userId) ids.add(selectedThread.userId);
    if (selectedThread?.id) ids.add(selectedThread.id);

    const missing = Array.from(ids).filter((id) => !(id in userProfiles));
    if (!missing.length) return () => {};

    let isMounted = true;
    const loadProfiles = async () => {
      try {
        const docs = await Promise.all(
          missing.map((uid) => getDoc(doc(db, "users", uid)))
        );
        if (!isMounted) return;
        const next = docs.reduce((acc, snap, index) => {
          acc[missing[index]] = snap.exists() ? snap.data() : null;
          return acc;
        }, {});
        if (Object.keys(next).length) {
          setUserProfiles((prev) => ({ ...prev, ...next }));
        }
      } catch (error) {
        console.error("Failed to load user profiles", error);
      }
    };

    loadProfiles();

    return () => {
      isMounted = false;
    };
  }, [
    bookings,
    chatThreads,
    creators,
    db,
    selectedThread?.id,
    selectedThread?.userId,
    userProfiles,
  ]);

  const getUserLabel = (userId, fallback) => {
    const profile = userId ? userProfiles[userId] : null;
    return (
      profile?.username ||
      profile?.name ||
      profile?.displayName ||
      fallback ||
      userId ||
      "User"
    );
  };

  useEffect(() => {
    if (!currentUser || !role) {
      setCreators([]);
      setLoadingCreators(false);
      return () => {};
    }

    setLoadingCreators(true);
    let unsubscribe;

    if (isCreatorRole) {
      const q = query(
        collection(db, "creators"),
        where("ownerId", "==", currentUser.uid),
        limit(1)
      );
      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const next = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));
          setCreators(next);
          setLoadingCreators(false);
        },
        (error) => {
          console.error("Failed to load creator profile", error);
          setCreators([]);
          setLoadingCreators(false);
        }
      );
    } else {
      const q = query(
        collection(db, "creators"),
        orderBy("createdAt", "desc"),
        limit(200)
      );
      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const next = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));
          setCreators(next);
          setLoadingCreators(false);
        },
        (error) => {
          console.error("Failed to load creators", error);
          setCreators([]);
          setLoadingCreators(false);
        }
      );
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser, isCreatorRole, role]);

  useEffect(() => {
    if (!selectedCreatorId && creators.length) {
      setSelectedCreatorId(creators[0].id);
      return;
    }
    if (
      selectedCreatorId &&
      !creators.some((item) => item.id === selectedCreatorId)
    ) {
      setSelectedCreatorId(creators[0]?.id || null);
    }
  }, [creators, selectedCreatorId]);

  useEffect(() => {
    if (!selectedCreatorId && activeModule !== "profile") {
      setActiveModule("profile");
    }
  }, [activeModule, selectedCreatorId]);

  useEffect(() => {
    setSelectedChatId(null);
    setChatMessages([]);
    setChatText("");
    setChatError("");
  }, [selectedCreatorId]);

  useEffect(() => {
    if (isCreatorRole && currentUser) {
      setCreatorDraft((prev) => ({
        ...prev,
        ownerId: currentUser.uid,
      }));
    }
  }, [currentUser, isCreatorRole]);

  useEffect(() => {
    setCreatorEditDraft(buildCreatorDraft(selectedCreator, currentUser?.uid));
    setAvatarEditFile(null);
    setAvatarEditPreview("");
    setAvatarEditUploadProgress(null);
  }, [selectedCreator, currentUser]);

  useEffect(() => {
    if (!avatarFile) {
      setAvatarPreview("");
      return () => {};
    }
    setAvatarUploadProgress(null);
    const previewUrl = URL.createObjectURL(avatarFile);
    setAvatarPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [avatarFile]);

  useEffect(() => {
    if (!avatarEditFile) {
      setAvatarEditPreview("");
      return () => {};
    }
    setAvatarEditUploadProgress(null);
    const previewUrl = URL.createObjectURL(avatarEditFile);
    setAvatarEditPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [avatarEditFile]);

  useEffect(() => {
    if (!selectedCreatorId) {
      setSessions([]);
      setSelectedSessionId(null);
      setIsCreatingSession(true);
      setLoadingSessions(false);
      return () => {};
    }

    setLoadingSessions(true);
    const q = query(
      collection(db, "creators", selectedCreatorId, "playSessions"),
      orderBy("startsAt", "asc"),
      limit(200)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setSessions(next);
        setLoadingSessions(false);
      },
      (error) => {
        console.error("Failed to load sessions", error);
        setSessions([]);
        setLoadingSessions(false);
      }
    );

    return () => unsubscribe();
  }, [selectedCreatorId]);

  useEffect(() => {
    if (!sessions.length) {
      setSelectedSessionId(null);
      setIsCreatingSession(true);
      return;
    }
    if (!selectedSessionId && !isCreatingSession && sessions.length) {
      setSelectedSessionId(sessions[0]?.id || null);
      return;
    }
    if (
      selectedSessionId &&
      !sessions.some((item) => item.id === selectedSessionId)
    ) {
      if (isCreatingSession) {
        setSelectedSessionId(null);
      } else {
        setSelectedSessionId(sessions[0]?.id || null);
      }
    }
  }, [isCreatingSession, selectedSessionId, sessions]);

  useEffect(() => {
    if (!selectedSession) {
      setSessionDraft(defaultSessionDraft(defaultGameId));
      return;
    }
    setSessionDraft({
      title: selectedSession.title || "",
      gameId: selectedSession.gameId || defaultGameId,
      startsAt: formatDateTimeInput(selectedSession.startsAt),
      durationMins: selectedSession.durationMins ?? "",
      capacity: selectedSession.capacity ?? "",
      price: selectedSession.price ?? "",
    });
  }, [defaultGameId, selectedSession]);

  useEffect(() => {
    if (!selectableGames.length) return;
    setSessionDraft((prev) => {
      if (!prev?.gameId) {
        return { ...prev, gameId: defaultGameId };
      }
      const exists = games.some((game) => game.id === prev.gameId);
      if (exists) return prev;
      return { ...prev, gameId: defaultGameId };
    });
  }, [defaultGameId, games, selectableGames]);

  useEffect(() => {
    if (!selectedCreatorId || !selectedSessionId) {
      setBookings([]);
      setLoadingBookings(false);
      return () => {};
    }

    setLoadingBookings(true);
    const q = query(
      collection(
        db,
        "creators",
        selectedCreatorId,
        "playSessions",
        selectedSessionId,
        "bookings"
      ),
      orderBy("bookedAt", "desc"),
      limit(200)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setBookings(next);
        setLoadingBookings(false);
      },
      (error) => {
        console.error("Failed to load bookings", error);
        setBookings([]);
        setLoadingBookings(false);
      }
    );

    return () => unsubscribe();
  }, [selectedCreatorId, selectedSessionId]);

  useEffect(() => {
    if (!selectedCreatorId || activeModule !== "chat") {
      setChatThreads([]);
      setSelectedChatId(null);
      setLoadingChats(false);
      return () => {};
    }

    setLoadingChats(true);
    const q = query(
      collection(db, "creators", selectedCreatorId, "chats"),
      orderBy("lastMessageAt", "desc"),
      limit(200)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setChatThreads(next);
        setLoadingChats(false);
      },
      (error) => {
        console.error("Failed to load chats", error);
        setChatThreads([]);
        setLoadingChats(false);
      }
    );

    return () => unsubscribe();
  }, [activeModule, selectedCreatorId]);

  useEffect(() => {
    if (activeModule !== "chat") return;
    if (!chatThreads.length) {
      setSelectedChatId(null);
      return;
    }
    if (!selectedChatId || !chatThreads.some((item) => item.id === selectedChatId)) {
      setSelectedChatId(chatThreads[0].id);
    }
  }, [activeModule, chatThreads, selectedChatId]);

  useEffect(() => {
    if (!selectedCreatorId || !selectedThread?.id || activeModule !== "chat") {
      setChatMessages([]);
      setLoadingChatMessages(false);
      return () => {};
    }

    setLoadingChatMessages(true);
    const q = query(
      collection(
        db,
        "creators",
        selectedCreatorId,
        "chats",
        selectedThread.id,
        "messages"
      ),
      orderBy("createdAt", "asc"),
      limit(500)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setChatMessages(next);
        setLoadingChatMessages(false);
      },
      (error) => {
        console.error("Failed to load chat messages", error);
        setChatMessages([]);
        setLoadingChatMessages(false);
      }
    );

    return () => unsubscribe();
  }, [activeModule, selectedCreatorId, selectedThread?.id]);

  const filteredCreators = useMemo(() => {
    if (!search.trim()) return creators;
    const q = normalize(search);
    return creators.filter((creator) => {
      const games = getCreatorGames(creator).map(getGameName).join(" ");
      return [
        creator.displayName,
        creator.username,
        getCreatorOwnerId(creator),
        games,
      ].some((value) => normalize(value).includes(q));
    });
  }, [creators, getGameName, search]);

  const creatorProfileExists = isCreatorRole && creators.length > 0;
  const canCreateCreator = isSuperRole || (isCreatorRole && !creatorProfileExists);
  const createDisabledReason = isCreatorRole
    ? "You already have a creator profile."
    : "You do not have permission to create creator profiles.";
  const hasSelectedCreator = Boolean(selectedCreatorId);
  const moduleTabs = [
    { id: "profile", label: "Creator Profile" },
    { id: "sessions", label: "Play Sessions", disabled: !hasSelectedCreator },
    { id: "bookings", label: "Bookings", disabled: !hasSelectedCreator },
    { id: "chat", label: "Chats", disabled: !hasSelectedCreator },
  ];
  const showDetailCard =
    activeModule === "profile" && (!isCreatorRole || creatorProfileExists);

  useEffect(() => {
    if (activeModule !== "profile" && showCreateForm) {
      setShowCreateForm(false);
      setCreateError("");
    }
  }, [activeModule, showCreateForm]);

  useEffect(() => {
    if (creatorProfileExists && showCreateForm) {
      setShowCreateForm(false);
      setCreateError("");
    }
  }, [creatorProfileExists, showCreateForm]);

  const toggleCreatorGame = (setter, gameId) => {
    setter((prev) => {
      const current = new Set(prev.games || []);
      if (current.has(gameId)) {
        current.delete(gameId);
      } else {
        current.add(gameId);
      }
      return { ...prev, games: Array.from(current) };
    });
  };

  const handleCreateCreator = async () => {
    if (!currentUser) return;
    if (!canCreateCreator) return;

    setCreatingCreator(true);
    setCreateError("");
    try {
      const ownerId = (creatorDraft.ownerId || currentUser.uid || "").trim();
      const displayName = (
        isCreatorRole
          ? lockedDisplayName || creatorDraft.displayName
          : creatorDraft.displayName
      ).trim();
      const username = (
        isCreatorRole
          ? lockedUsername || creatorDraft.username
          : creatorDraft.username
      ).trim();
      if (!ownerId || !displayName || !username) {
        setCreateError(
          isCreatorRole
            ? "Complete your user profile (name and username) to create a creator profile."
            : "Display name, username, and owner UID are required."
        );
        setCreatingCreator(false);
        return;
      }

      const startingPrice = parseNumber(creatorDraft.startingPrice);
      const priceMin = parseNumber(creatorDraft.priceMin);
      const priceMax = parseNumber(creatorDraft.priceMax);
      let avatarUrl = null;
      if (avatarFile) {
        try {
          setAvatarUploadProgress(0);
          avatarUrl = await uploadCreatorAvatar(
            avatarFile,
            ownerId,
            setAvatarUploadProgress
          );
        } catch (error) {
          setCreateError("Failed to upload avatar image.");
          setAvatarUploadProgress(null);
          setCreatingCreator(false);
          return;
        }
      }
      const minRange =
        priceMin !== null
          ? priceMin
          : startingPrice !== null
          ? startingPrice
          : null;
      const priceRange =
        minRange !== null || priceMax !== null
          ? { min: minRange, max: priceMax }
          : null;

      await addDoc(collection(db, "creators"), {
        displayName,
        username,
        avatar: avatarUrl,
        bio: creatorDraft.bio.trim() || null,
        games: Array.from(new Set(creatorDraft.games || [])),
        startingPrice: startingPrice ?? null,
        priceRange,
        isOnline: Boolean(creatorDraft.isOnline),
        isLive: Boolean(creatorDraft.isLive),
        ownerId,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        createdByRole: role || null,
      });

      setCreatorDraft((prev) => ({
        ...buildCreatorDraft(null, isCreatorRole ? currentUser.uid : ""),
        ownerId: prev.ownerId,
      }));
      setShowCreateForm(false);
      setAvatarFile(null);
      setAvatarPreview("");
      setAvatarUploadProgress(null);
    } catch (error) {
      console.error("Failed to create creator", error);
      setCreateError("Failed to create creator profile.");
    } finally {
      setCreatingCreator(false);
    }
  };

  const handleUpdateCreator = async () => {
    if (!currentUser || !selectedCreator) return;

    setUpdatingCreator(true);
    setUpdateError("");
    try {
      const displayName = (
        isCreatorRole
          ? lockedDisplayName || creatorEditDraft.displayName
          : creatorEditDraft.displayName
      ).trim();
      const username = (
        isCreatorRole
          ? lockedUsername || creatorEditDraft.username
          : creatorEditDraft.username
      ).trim();
      if (!displayName || !username) {
        setUpdateError(
          isCreatorRole
            ? "Complete your user profile (name and username) to update the creator profile."
            : "Display name and username are required."
        );
        setUpdatingCreator(false);
        return;
      }

      const startingPrice = parseNumber(creatorEditDraft.startingPrice);
      const priceMin = parseNumber(creatorEditDraft.priceMin);
      const priceMax = parseNumber(creatorEditDraft.priceMax);
      const ownerId =
        getCreatorOwnerId(selectedCreator) || currentUser?.uid || "unknown";
      let avatarUrl = creatorEditDraft.avatar.trim() || null;
      if (avatarEditFile) {
        try {
          setAvatarEditUploadProgress(0);
          avatarUrl = await uploadCreatorAvatar(
            avatarEditFile,
            ownerId,
            setAvatarEditUploadProgress
          );
        } catch (error) {
          setUpdateError("Failed to upload avatar image.");
          setAvatarEditUploadProgress(null);
          setUpdatingCreator(false);
          return;
        }
      }
      const minRange =
        priceMin !== null
          ? priceMin
          : startingPrice !== null
          ? startingPrice
          : null;
      const priceRange =
        minRange !== null || priceMax !== null
          ? { min: minRange, max: priceMax }
          : null;

      await updateDoc(doc(db, "creators", selectedCreator.id), {
        displayName,
        username,
        avatar: avatarUrl,
        bio: creatorEditDraft.bio.trim() || null,
        games: Array.from(new Set(creatorEditDraft.games || [])),
        startingPrice: startingPrice ?? null,
        priceRange,
        isOnline: Boolean(creatorEditDraft.isOnline),
        isLive: Boolean(creatorEditDraft.isLive),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
        updatedByRole: role || null,
      });
      setAvatarEditFile(null);
      setAvatarEditPreview("");
      setAvatarEditUploadProgress(null);
    } catch (error) {
      console.error("Failed to update creator", error);
      setUpdateError("Failed to update creator profile.");
    } finally {
      setUpdatingCreator(false);
    }
  };

  const handleSaveSession = async () => {
    if (!currentUser || !selectedCreatorId) return;

    setSessionSaving(true);
    setSessionError("");
    try {
      if (!sessionDraft.startsAt) {
        setSessionError("Start time is required for a session.");
        setSessionSaving(false);
        return;
      }

      const startsAtDate = new Date(sessionDraft.startsAt);
      if (Number.isNaN(startsAtDate.getTime())) {
        setSessionError("Invalid start time.");
        setSessionSaving(false);
        return;
      }

      const capacityValue = parseNumber(sessionDraft.capacity);
      if (
        selectedSession &&
        capacityValue !== null &&
        capacityValue < Number(selectedSession.bookedCount || 0)
      ) {
        setSessionError("Capacity cannot be lower than booked slots.");
        setSessionSaving(false);
        return;
      }

      const payload = {
        title: sessionDraft.title.trim() || "Play Session",
        gameId: sessionDraft.gameId || defaultGameId,
        startsAt: Timestamp.fromDate(startsAtDate),
        durationMins: parseNumber(sessionDraft.durationMins),
        capacity: capacityValue,
        price: parseNumber(sessionDraft.price) ?? 0,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
        updatedByRole: role || null,
      };

      if (selectedSession && !isCreatingSession) {
        await updateDoc(
          doc(
            db,
            "creators",
            selectedCreatorId,
            "playSessions",
            selectedSession.id
          ),
          payload
        );
      } else {
        await addDoc(
          collection(db, "creators", selectedCreatorId, "playSessions"),
          {
            ...payload,
            bookedCount: 0,
            createdAt: serverTimestamp(),
            createdBy: currentUser.uid,
            createdByRole: role || null,
          }
        );
      }

      if (!selectedSession || isCreatingSession) {
        setSessionDraft(defaultSessionDraft(defaultGameId));
      }
    } catch (error) {
      console.error("Failed to save session", error);
      setSessionError("Failed to save session.");
    } finally {
      setSessionSaving(false);
    }
  };

  const handleDeleteSession = async (session) => {
    if (!currentUser || !selectedCreatorId || !session) return;

    const hasBookings = Number(session.bookedCount || 0) > 0;
    const message = hasBookings
      ? "This session has bookings. Deleting it will not remove booking records. Delete anyway?"
      : "Delete this session?";
    if (!window.confirm(message)) return;

    setDeletingSessionId(session.id);
    setSessionError("");
    try {
      await deleteDoc(
        doc(db, "creators", selectedCreatorId, "playSessions", session.id)
      );
      if (selectedSessionId === session.id) {
        clearSessionSelection();
      }
    } catch (error) {
      console.error("Failed to delete session", error);
      setSessionError("Failed to delete session.");
    } finally {
      setDeletingSessionId(null);
    }
  };

  const clearSessionSelection = () => {
    setSelectedSessionId(null);
    setIsCreatingSession(true);
    setSessionDraft(defaultSessionDraft(defaultGameId));
    setSessionError("");
  };

  const handleSendChatMessage = async () => {
    if (!selectedCreatorId || !selectedThread || sendingChat) return;
    const trimmed = chatText.trim();
    if (!trimmed) return;

    const creatorOwnerId = getCreatorOwnerId(selectedCreator) || currentUser?.uid;
    if (!creatorOwnerId) {
      setChatError("Creator owner ID is missing.");
      return;
    }

    setSendingChat(true);
    setChatError("");
    const creatorName = getCreatorLabel(selectedCreator);
    const userId = selectedThread.userId || selectedThread.id || "";

    try {
      await addDoc(
        collection(
          db,
          "creators",
          selectedCreatorId,
          "chats",
          selectedThread.id,
          "messages"
        ),
        {
          text: trimmed,
          senderId: creatorOwnerId,
          senderName: creatorName,
          createdAt: serverTimestamp(),
        }
      );
      await setDoc(
        doc(db, "creators", selectedCreatorId, "chats", selectedThread.id),
        {
          creatorId: selectedCreatorId,
          creatorName: creatorName,
          userId: userId,
          userName: selectedThread.userName || selectedThread.username || null,
          lastMessage: trimmed,
          lastMessageAt: serverTimestamp(),
        },
        { merge: true }
      );
      setChatText("");
    } catch (error) {
      console.error("Failed to send chat message", error);
      setChatError("Failed to send message.");
    } finally {
      setSendingChat(false);
    }
  };

  const renderCreateCard = () => {
    const title = isCreatorRole
      ? "Create your creator profile"
      : "Create creator profile";
    const ctaLabel = isCreatorRole ? "Create profile" : "New creator";
    const submitLabel = isCreatorRole ? "Create profile" : "Create creator";
    const displayNameValue = isCreatorRole
      ? lockedDisplayName || creatorDraft.displayName
      : creatorDraft.displayName;
    const usernameValue = isCreatorRole
      ? lockedUsername || creatorDraft.username
      : creatorDraft.username;

    return (
      <div className="card form-card">
        <div className="card-header">
          <h3>{title}</h3>
          {canCreateCreator && showCreateForm ? (
            <div className="inline-actions">
              <button
                className="button ghost small"
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setCreateError("");
                }}
              >
                Hide form
              </button>
            </div>
          ) : null}
        </div>
        {!canCreateCreator ? (
          <div className="empty-inline">{createDisabledReason}</div>
        ) : !showCreateForm ? (
          <>
            <div className="empty-inline">
              {isCreatorRole
                ? "Create your profile to start offering play sessions."
                : "Create a new creator profile to manage sessions and bookings."}
            </div>
            <div className="form-actions">
              <button
                className="button primary"
                type="button"
                onClick={() => {
                  setShowCreateForm(true);
                  setCreateError("");
                }}
              >
                {ctaLabel}
              </button>
            </div>
          </>
        ) : (
          <>
            {createError ? (
              <div className="alert error">{createError}</div>
            ) : null}
            <div className="form-grid">
              <label className="field">
                <span>Display Name</span>
                <input
                  type="text"
                  value={displayNameValue}
                  onChange={(event) =>
                    setCreatorDraft((prev) => ({
                      ...prev,
                      displayName: event.target.value,
                    }))
                  }
                  disabled={isCreatorRole}
                />
              </label>
              <label className="field">
                <span>Username</span>
                <input
                  type="text"
                  value={usernameValue}
                  onChange={(event) =>
                    setCreatorDraft((prev) => ({
                      ...prev,
                      username: event.target.value,
                    }))
                  }
                  disabled={isCreatorRole}
                />
              </label>
              <label className="field">
                <span>Owner UID</span>
                <input
                  type="text"
                  value={creatorDraft.ownerId}
                  onChange={(event) =>
                    setCreatorDraft((prev) => ({
                      ...prev,
                      ownerId: event.target.value,
                    }))
                  }
                  disabled={isCreatorRole}
                />
              </label>
              <label className="field">
                <span>Starting Price (AP)</span>
                <input
                  type="number"
                  value={creatorDraft.startingPrice}
                  onChange={(event) =>
                    setCreatorDraft((prev) => ({
                      ...prev,
                      startingPrice: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="field">
                <span>Price Range Min (AP)</span>
                <input
                  type="number"
                  value={creatorDraft.priceMin}
                  onChange={(event) =>
                    setCreatorDraft((prev) => ({
                      ...prev,
                      priceMin: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="field">
                <span>Price Range Max (AP)</span>
                <input
                  type="number"
                  value={creatorDraft.priceMax}
                  onChange={(event) =>
                    setCreatorDraft((prev) => ({
                      ...prev,
                      priceMax: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="field field-wide">
                <span>Bio</span>
                <textarea
                  rows="3"
                  value={creatorDraft.bio}
                  onChange={(event) =>
                    setCreatorDraft((prev) => ({
                      ...prev,
                      bio: event.target.value,
                    }))
                  }
                />
              </label>
            </div>

            {isCreatorRole ? (
              <div className="helper-text">
                Name and username are synced from your user profile.
              </div>
            ) : null}

            <div className="form-section">
              <div className="section-title">Avatar</div>
              <div className="avatar-uploader">
                <div className="avatar-preview">
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="Avatar preview" />
                  ) : (
                    <div className="avatar-placeholder">No Image</div>
                  )}
                </div>
                <div className="avatar-actions">
                  <label className="button ghost small avatar-file">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) =>
                        setAvatarFile(event.target.files?.[0] || null)
                      }
                    />
                    Upload image
                  </label>
                  {avatarFile ? (
                    <button
                      className="button ghost small"
                      type="button"
                      onClick={() => {
                        setAvatarFile(null);
                        setAvatarPreview("");
                        setAvatarUploadProgress(null);
                      }}
                    >
                      Remove upload
                    </button>
                  ) : null}
                  {avatarUploadProgress !== null ? (
                    <div className="upload-progress">
                      Uploading {avatarUploadProgress}%
                    </div>
                  ) : (
                    <div className="helper-text">
                      Upload an image to set the avatar.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="section-title-row">
                <div className="section-title">Games</div>
                <span className="chip">
                  {(creatorDraft.games || []).length} selected
                </span>
              </div>
              <div className="game-picker">
                {games.map((game) => {
                  const isSelected = creatorDraft.games?.includes(game.id);
                  const isInactive = game.active === false;
                  const isDisabled = isInactive && !isSelected;
                  return (
                    <button
                      key={game.id}
                      type="button"
                      className={`game-chip ${isSelected ? "selected" : ""} ${
                        isDisabled ? "disabled" : ""
                      }`}
                      aria-pressed={isSelected}
                      disabled={isDisabled}
                      onClick={() => {
                        if (isDisabled) return;
                        toggleCreatorGame(setCreatorDraft, game.id);
                      }}
                    >
                      <span className="game-chip-icon">
                        {game.icon ? (
                          <img src={game.icon} alt="" />
                        ) : (
                          <span className="game-chip-fallback">
                            {game.name?.slice(0, 1) || "?"}
                          </span>
                        )}
                      </span>
                      <span className="game-chip-text">
                        <span className="game-chip-title">{game.name}</span>
                        <span className="game-chip-meta">
                          {isInactive ? "Inactive" : `ID ${game.id}`}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="form-section">
              <div className="section-title">Availability</div>
              <div className="stacked-list">
                <label className="list-row">
                  <div className="list-title">Online</div>
                  <span className="toggle">
                    <input
                      type="checkbox"
                      checked={creatorDraft.isOnline}
                      onChange={(event) =>
                        setCreatorDraft((prev) => ({
                          ...prev,
                          isOnline: event.target.checked,
                        }))
                      }
                    />
                    <span className="toggle-slider" aria-hidden="true" />
                  </span>
                </label>
                <label className="list-row">
                  <div className="list-title">Live</div>
                  <span className="toggle">
                    <input
                      type="checkbox"
                      checked={creatorDraft.isLive}
                      onChange={(event) =>
                        setCreatorDraft((prev) => ({
                          ...prev,
                          isLive: event.target.checked,
                        }))
                      }
                    />
                    <span className="toggle-slider" aria-hidden="true" />
                  </span>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button
                className="button ghost"
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setCreateError("");
                }}
                disabled={creatingCreator}
              >
                Cancel
              </button>
              <button
                className="button primary"
                type="button"
                onClick={handleCreateCreator}
                disabled={creatingCreator}
              >
                {creatingCreator ? "Saving..." : submitLabel}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Creators</h1>
          <p>
            {isCreatorRole
              ? "Manage your profile, play sessions, and bookings."
              : "Manage creator profiles, play sessions, and booking activity."}
          </p>
        </div>
        <div className="page-actions">
          {!isCreatorRole ? (
            <input
              className="search-input"
              type="search"
              placeholder="Search creators"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          ) : null}
        </div>
      </div>

      <div className="manage-tabs" role="tablist" aria-label="Creator modules">
        {moduleTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeModule === tab.id}
            className={`manage-tab ${activeModule === tab.id ? "active" : ""}`}
            onClick={() => setActiveModule(tab.id)}
            disabled={tab.disabled}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="manage-grid">
        {!isCreatorRole ? (
          <div className="manage-column">
            <div className="card table-card">
              <div className="card-header">
                <h3>Creators</h3>
                <span className="chip">{filteredCreators.length} total</span>
              </div>
              {loadingCreators ? (
                <div className="empty-inline">Loading creators...</div>
              ) : filteredCreators.length === 0 ? (
                <div className="empty-inline">No creators found.</div>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Creator</th>
                        <th>Owner</th>
                        <th>Games</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCreators.map((creator) => {
                        const games = getCreatorGames(creator)
                          .map(getGameName)
                          .join(", ");
                        const statusLabel = creator.isLive
                          ? "Live"
                          : creator.isOnline
                          ? "Online"
                          : "Offline";
                        const statusClass = creator.isLive
                          ? "accent"
                          : creator.isOnline
                          ? "success"
                          : "muted";
                        const ownerId = getCreatorOwnerId(creator);
                        const ownerLabel = getUserLabel(ownerId);
                        return (
                          <tr
                            key={creator.id}
                            className={
                              selectedCreatorId === creator.id ? "active-row" : ""
                            }
                            onClick={() => setSelectedCreatorId(creator.id)}
                          >
                            <td>
                              <div className="table-title">
                                {creator.displayName || "Unnamed"}
                              </div>
                              <div className="table-subtitle">
                                @{creator.username || "username"}
                              </div>
                            </td>
                            <td title={ownerId || ""}>{ownerLabel || "N/A"}</td>
                            <td>{games || "N/A"}</td>
                            <td>
                              <span className={`status-pill ${statusClass}`}>
                                {statusLabel}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {activeModule === "profile" ? renderCreateCard() : null}
          </div>
        ) : null}

        <div className={`manage-column ${isCreatorRole ? "manage-full" : ""}`}>
          {activeModule === "profile" && isCreatorRole && !creatorProfileExists
            ? renderCreateCard()
            : null}
          {showDetailCard ? (
            <div className="card detail-card">
            <div className="card-header">
              <h3>{isCreatorRole ? "Your profile" : "Creator details"}</h3>
              {selectedCreator ? (
                <span className="chip">{selectedCreator.id}</span>
              ) : null}
            </div>
            {!selectedCreator ? (
              <div className="empty-inline">
                {isCreatorRole
                  ? "Loading your creator profile..."
                  : "Select a creator to view and edit profile details."}
              </div>
            ) : (
              <>
                {updateError ? (
                  <div className="alert error">{updateError}</div>
                ) : null}
                <div className="form-grid">
                  <label className="field">
                    <span>Display Name</span>
                    <input
                      type="text"
                      value={
                        isCreatorRole
                          ? lockedDisplayName || creatorEditDraft.displayName
                          : creatorEditDraft.displayName
                      }
                      onChange={(event) =>
                        setCreatorEditDraft((prev) => ({
                          ...prev,
                          displayName: event.target.value,
                        }))
                      }
                      disabled={isCreatorRole}
                    />
                  </label>
                  <label className="field">
                    <span>Username</span>
                    <input
                      type="text"
                      value={
                        isCreatorRole
                          ? lockedUsername || creatorEditDraft.username
                          : creatorEditDraft.username
                      }
                      onChange={(event) =>
                        setCreatorEditDraft((prev) => ({
                          ...prev,
                          username: event.target.value,
                        }))
                      }
                      disabled={isCreatorRole}
                    />
                  </label>
                  <label className="field">
                    <span>Owner UID</span>
                    <input type="text" value={creatorEditDraft.ownerId} disabled />
                  </label>
                  <label className="field">
                    <span>Starting Price (AP)</span>
                    <input
                      type="number"
                      value={creatorEditDraft.startingPrice}
                      onChange={(event) =>
                        setCreatorEditDraft((prev) => ({
                          ...prev,
                          startingPrice: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Price Range Min (AP)</span>
                    <input
                      type="number"
                      value={creatorEditDraft.priceMin}
                      onChange={(event) =>
                        setCreatorEditDraft((prev) => ({
                          ...prev,
                          priceMin: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Price Range Max (AP)</span>
                    <input
                      type="number"
                      value={creatorEditDraft.priceMax}
                      onChange={(event) =>
                        setCreatorEditDraft((prev) => ({
                          ...prev,
                          priceMax: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="field field-wide">
                    <span>Bio</span>
                    <textarea
                      rows="3"
                      value={creatorEditDraft.bio}
                      onChange={(event) =>
                        setCreatorEditDraft((prev) => ({
                          ...prev,
                          bio: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>

                {isCreatorRole ? (
                  <div className="helper-text">
                    Name and username are synced from your user profile.
                  </div>
                ) : null}

                <div className="form-section">
                  <div className="section-title">Avatar</div>
                  <div className="avatar-uploader">
                    <div className="avatar-preview">
                      {avatarEditPreview ? (
                        <img src={avatarEditPreview} alt="Avatar preview" />
                      ) : creatorEditDraft.avatar ? (
                        <img src={creatorEditDraft.avatar} alt="Avatar preview" />
                      ) : (
                        <div className="avatar-placeholder">No Image</div>
                      )}
                    </div>
                    <div className="avatar-actions">
                      <label className="button ghost small avatar-file">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(event) =>
                            setAvatarEditFile(event.target.files?.[0] || null)
                          }
                        />
                        Upload image
                      </label>
                      {avatarEditFile ? (
                        <button
                          className="button ghost small"
                          type="button"
                          onClick={() => {
                            setAvatarEditFile(null);
                            setAvatarEditPreview("");
                            setAvatarEditUploadProgress(null);
                          }}
                        >
                          Remove upload
                        </button>
                      ) : null}
                      {avatarEditUploadProgress !== null ? (
                        <div className="upload-progress">
                          Uploading {avatarEditUploadProgress}%
                        </div>
                      ) : (
                        <div className="helper-text">
                          Upload to replace the current avatar.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-title-row">
                    <div className="section-title">Games</div>
                    <span className="chip">
                      {(creatorEditDraft.games || []).length} selected
                    </span>
                  </div>
                  <div className="game-picker">
                    {games.map((game) => {
                      const isSelected = creatorEditDraft.games?.includes(
                        game.id
                      );
                      const isInactive = game.active === false;
                      const isDisabled = isInactive && !isSelected;
                      return (
                        <button
                          key={game.id}
                          type="button"
                          className={`game-chip ${isSelected ? "selected" : ""} ${
                            isDisabled ? "disabled" : ""
                          }`}
                          aria-pressed={isSelected}
                          disabled={isDisabled}
                          onClick={() => {
                            if (isDisabled) return;
                            toggleCreatorGame(setCreatorEditDraft, game.id);
                          }}
                        >
                          <span className="game-chip-icon">
                            {game.icon ? (
                              <img src={game.icon} alt="" />
                            ) : (
                              <span className="game-chip-fallback">
                                {game.name?.slice(0, 1) || "?"}
                              </span>
                            )}
                          </span>
                          <span className="game-chip-text">
                            <span className="game-chip-title">{game.name}</span>
                            <span className="game-chip-meta">
                              {isInactive ? "Inactive" : `ID ${game.id}`}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-title">Availability</div>
                  <div className="stacked-list">
                    <label className="list-row">
                      <div className="list-title">Online</div>
                      <span className="toggle">
                        <input
                          type="checkbox"
                          checked={creatorEditDraft.isOnline}
                          onChange={(event) =>
                            setCreatorEditDraft((prev) => ({
                              ...prev,
                              isOnline: event.target.checked,
                            }))
                          }
                        />
                        <span className="toggle-slider" aria-hidden="true" />
                      </span>
                    </label>
                    <label className="list-row">
                      <div className="list-title">Live</div>
                      <span className="toggle">
                        <input
                          type="checkbox"
                          checked={creatorEditDraft.isLive}
                          onChange={(event) =>
                            setCreatorEditDraft((prev) => ({
                              ...prev,
                              isLive: event.target.checked,
                            }))
                          }
                        />
                        <span className="toggle-slider" aria-hidden="true" />
                      </span>
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    className="button primary"
                    type="button"
                    onClick={handleUpdateCreator}
                    disabled={updatingCreator}
                  >
                    {updatingCreator ? "Saving..." : "Update profile"}
                  </button>
                </div>
              </>
            )}
          </div>
          ) : null}

          {activeModule === "sessions" ? (
            <div className="card">
            <div className="card-header">
              <h3>Play sessions</h3>
              <div className="inline-actions">
                {selectedSession ? (
                  <button
                    className="button ghost small"
                    type="button"
                    onClick={clearSessionSelection}
                  >
                    New session
                  </button>
                ) : null}
              </div>
            </div>
            {!selectedCreator ? (
              <div className="empty-inline">
                Select a creator to manage sessions.
              </div>
            ) : (
              <>
                {sessionError ? (
                  <div className="alert error">{sessionError}</div>
                ) : null}
                <div className="form-grid">
                  <label className="field">
                    <span>Session Title</span>
                    <input
                      type="text"
                      value={sessionDraft.title}
                      onChange={(event) =>
                        setSessionDraft((prev) => ({
                          ...prev,
                          title: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Game</span>
                    <select
                      value={sessionDraft.gameId}
                      onChange={(event) =>
                        setSessionDraft((prev) => ({
                          ...prev,
                          gameId: event.target.value,
                        }))
                      }
                    >
                      {selectableGames.map((game) => (
                        <option key={game.id} value={game.id}>
                          {game.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span>Starts At</span>
                    <input
                      type="datetime-local"
                      value={sessionDraft.startsAt}
                      onChange={(event) =>
                        setSessionDraft((prev) => ({
                          ...prev,
                          startsAt: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Duration (mins)</span>
                    <input
                      type="number"
                      value={sessionDraft.durationMins}
                      onChange={(event) =>
                        setSessionDraft((prev) => ({
                          ...prev,
                          durationMins: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Capacity</span>
                    <input
                      type="number"
                      value={sessionDraft.capacity}
                      onChange={(event) =>
                        setSessionDraft((prev) => ({
                          ...prev,
                          capacity: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Price (AP)</span>
                    <input
                      type="number"
                      value={sessionDraft.price}
                      onChange={(event) =>
                        setSessionDraft((prev) => ({
                          ...prev,
                          price: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>

                <div className="form-actions">
                  <button
                    className="button primary"
                    type="button"
                    onClick={handleSaveSession}
                    disabled={sessionSaving}
                  >
                    {sessionSaving
                      ? "Saving..."
                      : selectedSession
                      ? "Update session"
                      : "Create session"}
                  </button>
                </div>

                <div className="divider" />

                {loadingSessions ? (
                  <div className="empty-inline">Loading sessions...</div>
                ) : sessions.length === 0 ? (
                  <div className="empty-inline">No sessions yet.</div>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Session</th>
                          <th>Schedule</th>
                          <th>Slots</th>
                          <th>Price</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessions.map((session) => {
                          const slots = `${session.bookedCount || 0}/${
                            session.capacity || "∞"
                          }`;
                          return (
                            <tr
                              key={session.id}
                              className={
                                selectedSessionId === session.id
                                  ? "active-row"
                                  : ""
                              }
                              onClick={() => {
                                setSelectedSessionId(session.id);
                                setIsCreatingSession(false);
                              }}
                            >
                              <td>
                                <div className="table-title">
                                  {session.title || "Play Session"}
                                </div>
                                <div className="table-subtitle">
                                  {getGameName(String(session.gameId || ""))}
                                </div>
                              </td>
                              <td>{formatDateTime(session.startsAt)}</td>
                              <td>{slots}</td>
                              <td>{session.price || 0} AP</td>
                              <td>
                                <div className="table-actions">
                                  <button
                                    className="button ghost small"
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setSelectedSessionId(session.id);
                                      setIsCreatingSession(false);
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="button danger small"
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleDeleteSession(session);
                                    }}
                                    disabled={deletingSessionId === session.id}
                                  >
                                    {deletingSessionId === session.id
                                      ? "Deleting..."
                                      : "Delete"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
          ) : null}

          {activeModule === "bookings" ? (
            <div className="card">
            <div className="card-header">
              <h3>Bookings</h3>
              {selectedSession ? (
                <span className="chip">
                  {bookings.length} bookings
                </span>
              ) : null}
            </div>
            {!selectedSession ? (
              <div className="empty-inline">
                Select a session from Play Sessions to view bookings.
              </div>
            ) : loadingBookings ? (
              <div className="empty-inline">Loading bookings...</div>
            ) : bookings.length === 0 ? (
              <div className="empty-inline">No bookings yet.</div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Status</th>
                      <th>Price</th>
                      <th>Booked At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => {
                      const status = String(booking.status || "confirmed");
                      const statusClass =
                        status.toLowerCase() === "confirmed"
                          ? "success"
                          : status.toLowerCase() === "cancelled"
                          ? "danger"
                          : "warning";
                      const bookingUserLabel = getUserLabel(
                        booking.userId,
                        booking.username
                      );
                      return (
                        <tr key={booking.id}>
                          <td>
                            <div className="table-title">
                              {bookingUserLabel}
                            </div>
                          </td>
                          <td>
                            <span className={`status-pill ${statusClass}`}>
                              {status}
                            </span>
                          </td>
                          <td>{booking.price || booking.amount || 0} AP</td>
                          <td>{formatDateTime(booking.bookedAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          ) : null}

          {activeModule === "chat" ? (
            <div className="card chat-card">
              <div className="card-header">
                <h3>Creator chat</h3>
                {selectedCreator ? (
                  <span className="chip">{getCreatorLabel(selectedCreator)}</span>
                ) : null}
              </div>
              {!selectedCreator ? (
                <div className="empty-inline">
                  Select a creator to manage chats.
                </div>
              ) : (
                <div className="chat-grid">
                  <div className="chat-thread-panel">
                    <div className="section-title">Active chats</div>
                    {loadingChats ? (
                      <div className="empty-inline">Loading chats...</div>
                    ) : chatThreads.length === 0 ? (
                      <div className="empty-inline">No chats yet.</div>
                    ) : (
                      <div className="chat-thread-list">
                        {chatThreads.map((thread) => {
                          const threadLabel = getThreadUserLabel(
                            thread,
                            userProfiles
                          );
                          const isActive = thread.id === selectedChatId;
                          return (
                            <button
                              key={thread.id}
                              type="button"
                              className={`chat-thread ${isActive ? "active" : ""}`}
                              onClick={() => setSelectedChatId(thread.id)}
                            >
                              <div className="chat-thread-title">
                                {threadLabel}
                              </div>
                              <div className="chat-thread-meta">
                                <span className="chat-thread-preview">
                                  {thread.lastMessage || "No messages yet"}
                                </span>
                                <span className="chat-thread-time">
                                  {formatTime(thread.lastMessageAt)}
                                </span>
                              </div>
                              {thread.sessionId ? (
                                <div className="chat-thread-session">
                                  Session: {thread.sessionId}
                                </div>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="chat-panel">
                    {!selectedThread ? (
                      <div className="empty-inline">
                        Select a chat to read and reply.
                      </div>
                    ) : (
                      <>
                        <div className="chat-panel-header">
                          <div>
                            <div className="table-title">
                              {getThreadUserLabel(selectedThread, userProfiles)}
                            </div>
                            <div className="table-subtitle">
                              User:{" "}
                              {getUserLabel(
                                selectedThread.userId || selectedThread.id
                              )}
                            </div>
                          </div>
                          <span className="chip">
                            {selectedThread.lastMessageAt
                              ? formatDateTime(selectedThread.lastMessageAt)
                              : "New chat"}
                          </span>
                        </div>

                        {chatError ? <div className="alert error">{chatError}</div> : null}

                        <div className="chat-messages">
                          {loadingChatMessages ? (
                            <div className="empty-inline">
                              Loading messages...
                            </div>
                          ) : chatMessages.length === 0 ? (
                            <div className="empty-inline">
                              No messages yet.
                            </div>
                          ) : (
                            chatMessages.map((message) => {
                              const creatorSenderId =
                                getCreatorOwnerId(selectedCreator) ||
                                currentUser?.uid ||
                                "";
                              const isOwn = message.senderId === creatorSenderId;
                              const senderLabel = isOwn
                                ? getCreatorLabel(selectedCreator)
                                : message.senderName ||
                                  getThreadUserLabel(
                                    selectedThread,
                                    userProfiles
                                  );

                              return (
                                <div
                                  key={message.id}
                                  className={`chat-message ${isOwn ? "own" : ""}`}
                                >
                                  <div className="chat-message-author">
                                    {senderLabel}
                                  </div>
                                  <div className="chat-message-text">
                                    {message.text || ""}
                                  </div>
                                  <div className="chat-message-time">
                                    {formatTime(message.createdAt)}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>

                        <div className="chat-composer">
                          <textarea
                            className="chat-input"
                            rows={3}
                            value={chatText}
                            onChange={(event) => setChatText(event.target.value)}
                            placeholder="Write a reply..."
                          />
                          <button
                            className="button primary chat-send"
                            type="button"
                            onClick={handleSendChatMessage}
                            disabled={sendingChat || !chatText.trim()}
                          >
                            {sendingChat ? "Sending..." : "Send"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Creators;
